import { useEffect, useMemo, useState } from "react";

// Third party
import { useMutation, useQuery } from "@tanstack/react-query";

// MUI
import { Stack } from "@mui/material";

// API
import { deletePayrollProvidersAwaitingAccounts } from "@/Settings/api/AdministrationTools/mutations";

// Hooks
import { useLocation } from "@/Common/hooks/Location/useLocation";

// Components
import ProviderAuthenticatorItem from "./ProviderAuthenticatorItem";

// Helpers
import { and } from "@/Common/helpers/sonar";

// API
import { getProviderList, TGetProviderList } from "@/Settings/api/AdministrationTools/queries";

// Types
import { IIntegrationProvider } from "@/Settings/types/administrationTools/model";
import { Nullish } from "@/Common/interfaces/types";

interface IMultipleProviderAuthenticatorProps {
	locationId: string | undefined;
	isExpanded: boolean;
	payrollProviders: IIntegrationProvider[];
}

const DEFAULT_PROVIDER: IIntegrationProvider = {
	provider: "",
	providerName: "",
	services: [],
	authRoleStatuses: [],
	configuredAccounts: [],
};

const MIN_PROVIDERS = 1;
const MAX_PROVIDERS = 10;

const getPayrollProviders = (payrollProviders: IIntegrationProvider[] | undefined) => {
	if (!payrollProviders?.length) {
		return [DEFAULT_PROVIDER];
	}
	return [...payrollProviders];
};

const getSelectedProviders = (
	payrollProviders: IIntegrationProvider[] | undefined,
	selectedProviders: TGetProviderList[],
	providersOptions: Nullish<TGetProviderList[]> | undefined
) => {
	if (!payrollProviders?.length || !providersOptions?.length) {
		return [];
	}
	const hasSomePayrollProvider = payrollProviders?.some((providerItem) => !!providerItem?.providerName);
	if (!hasSomePayrollProvider) {
		return [];
	}
	if (!selectedProviders?.length) {
		return providersOptions.filter((option) => payrollProviders.some((provider) => provider.providerName === option.providerName));
	}
	return providersOptions.filter((option) => selectedProviders.some((selected) => selected.providerName === option.providerName));
};

const isProviderLimitReached = (payrollProviders: IIntegrationProvider[]) => {
	return payrollProviders.length >= MAX_PROVIDERS;
};

const shouldDisableDeleteProvider = (payrollProviders: IIntegrationProvider[]) => {
	return payrollProviders?.length <= MIN_PROVIDERS;
};

const ProviderAuthenticatorGroup = (props: IMultipleProviderAuthenticatorProps) => {
	const { locationId, isExpanded, payrollProviders: payrollProvidersOptions } = props;
	const location = useLocation();

	const [payrollProviders, setPayrollProviders] = useState<IIntegrationProvider[]>([]);
	const [selectedProviders, setSelectedProviders] = useState<TGetProviderList[]>([]);

	/***************************************** QUERIES ****************************************/
	const { data: providersOptions, isLoading } = useQuery({
		queryKey: ["providersOptions", locationId],
		queryFn: async () => getProviderList(locationId as string, "PP"),
		enabled: and(!!locationId) && isExpanded,
		staleTime: Infinity,
	});

	/***************************************** MUTATIONS **************************************/
	const { mutate: deleteAwaitingAccountMutate } = useMutation({
		mutationFn: async (params: { locationId: string; providerName: string; displayId: string }) => {
			return deletePayrollProvidersAwaitingAccounts(params.locationId, params.providerName, params.displayId);
		},
	});

	/***************************************** HANDLERS ***************************************/

	const handleAddProvider = () => {
		setPayrollProviders((prev) => [...prev, DEFAULT_PROVIDER]);
	};

	const handleDeleteProvider = (index: number) => {
		setPayrollProviders((prev) => prev.filter((_, i) => i !== index));
	};

	const handleUpdateSelectedProviders = (index: number, updatedProvider: TGetProviderList) => {
		const updatedSelectedProviders = [...(selectedProviders ?? [])];
		updatedSelectedProviders[index] = updatedProvider;
		setSelectedProviders(updatedSelectedProviders);
	};

	const handleDeleteAwaitingAccount = (providerName: string, displayId: string) => {
		if (!locationId) {
			return;
		}
		deleteAwaitingAccountMutate({ locationId, providerName, displayId });
	};

	/***************************************** EFFECTS ****************************************/
	const disabledOptions = useMemo(() => {
		if (!selectedProviders?.length) {
			return [];
		}
		return selectedProviders.map((provider) => provider.provider);
	}, [selectedProviders]);

	useEffect(() => {
		const initialPayrollProviders = getPayrollProviders(payrollProvidersOptions);
		setPayrollProviders(initialPayrollProviders);
		const newSelectedProviders = getSelectedProviders(initialPayrollProviders, selectedProviders, providersOptions);
		setSelectedProviders(newSelectedProviders);
	}, [providersOptions, payrollProvidersOptions]);

	return (
		<Stack display={"flex"} direction={"row"} alignItems={"flex-start"} flexWrap={"wrap"} rowGap={2}>
			{payrollProviders.map((payrollProvider, index) => {
				const keyIndex = `providerAuth-${index}`;
				const isLastItem = index === payrollProviders.length - 1;
				return (
					<ProviderAuthenticatorItem
						key={keyIndex}
						index={index}
						locationId={locationId}
						selectedProvider={selectedProviders[index]}
						providersOptions={providersOptions ?? []}
						payrollProvider={payrollProvider}
						isLoading={isLoading}
						isLastItem={isLastItem}
						disabledOptions={disabledOptions}
						shouldDisableDeleteProvider={shouldDisableDeleteProvider(payrollProviders)}
						isProviderLimitReached={isProviderLimitReached(payrollProviders)}
						handleAddProvider={handleAddProvider}
						handleDeleteProvider={handleDeleteProvider}
						handleUpdateSelectedProviders={handleUpdateSelectedProviders}
						handleDeleteAwaitingAccount={handleDeleteAwaitingAccount}
						timezone={location?.timezone}
					/>
				);
			})}
		</Stack>
	);
};

export default ProviderAuthenticatorGroup;
